import { useState } from 'react';
import {
  Button,
  Group,
  Modal,
  Stack,
  Table,
  TextInput,
  Title,
  ActionIcon,
  Text,
  Skeleton,
  Card,
  NumberInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { ZodSchema } from 'zod';
import { ReactNode } from 'react';
import { useCreate, useList, useRemove, useUpdate } from '../../api/hooks/useCrud.ts';
import { HttpError } from '../../api/client.ts';

/**
 * Single-page CRUD UI driven by a config object. Used for the simple
 * single-table resources: Countries, Languages, Locations,
 * AccomodationPlaces, ReportMails. (Roles needs its own page because of
 * the users membership.)
 */

export type FieldSpec =
  | { kind: 'text'; key: string; label: string; required?: boolean }
  | { kind: 'number'; key: string; label: string; required?: boolean };

export interface CrudConfig<T extends { id?: number | null }> {
  title: string;
  resource: string;
  schema: ZodSchema<T>;
  columns: Array<{ key: keyof T; header: string }>;
  fields: FieldSpec[];
  /** Default empty form values for "new" mode. */
  emptyValues: Partial<T>;
  /** Optional per-row extra actions, e.g. "Send now" on Report Mails. */
  rowActions?: Array<{
    label: string;
    icon: ReactNode;
    /** Runs when the user clicks the action; receives the row. */
    onRun(row: T): Promise<void> | void;
  }>;
}

export function GenericCrud<T extends { id?: number | null }>(props: { config: CrudConfig<T> }) {
  const { config } = props;
  const list = useList<T>(config.resource);
  const create = useCreate<T>(config.resource);
  const update = useUpdate<T>(config.resource);
  const remove = useRemove(config.resource);

  const [editing, setEditing] = useState<T | 'new' | null>(null);

  function openEdit(row: T) { setEditing(row); }
  function openNew() { setEditing('new'); }
  function close() { setEditing(null); }

  function onDelete(id: number) {
    modals.openConfirmModal({
      title: 'Delete',
      children: <Text size="sm">Are you sure? This cannot be undone.</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        remove.mutate(id, {
          onSuccess: () => notifications.show({ message: 'Deleted', color: 'green' }),
          onError: (err) => notifications.show({ message: errorMessage(err), color: 'red' }),
        });
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>{config.title}</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openNew}>
          New
        </Button>
      </Group>

      <Card withBorder padding={0} radius="md">
        {list.isPending ? (
          <Stack gap="xs" p="md">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} h={24} radius="sm" />)}
          </Stack>
        ) : list.isError ? (
          <Text p="md" c="red">{errorMessage(list.error)}</Text>
        ) : (
          <Table.ScrollContainer minWidth={500}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  {config.columns.map(c => (
                    <Table.Th key={String(c.key)}>{c.header}</Table.Th>
                  ))}
                  <Table.Th style={{ width: 80 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data ?? []).map(row => (
                  <Table.Tr key={String(row.id)}>
                    {config.columns.map(c => (
                      <Table.Td key={String(c.key)}>{String(row[c.key] ?? '')}</Table.Td>
                    ))}
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        {(config.rowActions ?? []).map(a => (
                          <ActionIcon
                            key={a.label}
                            variant="subtle"
                            onClick={async () => {
                              try {
                                await a.onRun(row);
                              } catch (err) {
                                notifications.show({ message: errorMessage(err), color: 'red' });
                              }
                            }}
                            aria-label={a.label}
                            title={a.label}
                          >
                            {a.icon}
                          </ActionIcon>
                        ))}
                        <ActionIcon variant="subtle" onClick={() => openEdit(row)} aria-label="Edit">
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => row.id != null && onDelete(row.id)}
                          aria-label="Delete"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(list.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={config.columns.length + 1}>
                      <Text c="dimmed" ta="center" py="md">No rows yet.</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {editing !== null && (
        <EditModal
          config={config}
          initial={editing === 'new' ? config.emptyValues : editing}
          isNew={editing === 'new'}
          onClose={close}
          onSubmit={async (values) => {
            try {
              if (editing === 'new') {
                await create.mutateAsync(values as Partial<T>);
                notifications.show({ message: 'Created', color: 'green' });
              } else {
                await update.mutateAsync(values as T);
                notifications.show({ message: 'Updated', color: 'green' });
              }
              close();
            } catch (err) {
              notifications.show({ message: errorMessage(err), color: 'red' });
            }
          }}
        />
      )}
    </Stack>
  );
}

interface EditProps<T extends { id?: number | null }> {
  config: CrudConfig<T>;
  initial: Partial<T> | T;
  isNew: boolean;
  onClose(): void;
  onSubmit(values: Partial<T>): Promise<void>;
}

function EditModal<T extends { id?: number | null }>({ config, initial, isNew, onClose, onSubmit }: EditProps<T>) {
  const form = useForm<Record<string, unknown>>({ initialValues: { ...initial as Record<string, unknown> } });
  const [submitting, setSubmitting] = useState(false);

  return (
    <Modal opened onClose={onClose} title={isNew ? `New ${config.title}` : `Edit ${config.title}`} centered>
      <form
        onSubmit={form.onSubmit(async (values) => {
          setSubmitting(true);
          // A cleared Mantine NumberInput yields '' — coerce number fields to
          // null so the server's `number | null` validation doesn't 400.
          const cleaned: Record<string, unknown> = { ...values };
          for (const f of config.fields) {
            if (f.kind === 'number' && (cleaned[f.key] === '' || cleaned[f.key] === undefined)) {
              cleaned[f.key] = null;
            }
          }
          await onSubmit(cleaned as Partial<T>);
          setSubmitting(false);
        })}
      >
        <Stack gap="sm">
          {config.fields.map(f => {
            if (f.kind === 'text') {
              return (
                <TextInput
                  key={f.key}
                  label={f.label}
                  required={f.required}
                  {...form.getInputProps(f.key)}
                />
              );
            }
            return (
              <NumberInput
                key={f.key}
                label={f.label}
                required={f.required}
                {...form.getInputProps(f.key)}
              />
            );
          })}
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={submitting}>Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    if (typeof err.body === 'object' && err.body && 'detail' in err.body) {
      return String((err.body as { detail: unknown }).detail);
    }
    return `${err.status} on ${err.url}`;
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}
